// Business › Huddle (BL-007): the office's morning huddle, as Brook's GP-tracker draws it. The pace
// to the month's revenue goal first, then the targets the office plans by, then each day: revenue
// earned (a job's revenue spread over the days it was worked), jobs finished and sold, working days
// passed and the day's note, which anyone here writes. The owner sets the month's goals.

import { useState, type ReactNode } from 'react';
import { Header, type NavProps } from '../Nav.tsx';
import { Loading, Money, Panel, useAct, useQuery, useToast } from '../books/ui.tsx';
import { monthName, monthOf, todayIn } from '../books/model.ts';
import { useDesign } from '../blueprint/design.ts';
import { HuddlePage } from '../blueprint/HuddlePage.tsx';
import { scrollFocus } from '../shared/scrollFocus.ts';
import {
  BASIS_WORDS,
  COUNTING,
  dayLabel,
  GOAL_FIELDS,
  goalInput,
  goalText,
  mayEditGoals,
  paceNotes,
  paceWords,
  stepMonth,
  type GoalField,
  type Huddle,
  type HuddleDay,
} from './huddle-model.ts';
import '@flowology/ui-web/base.css';
import '../books/books.css';
import '../blueprint/home.css';
import './huddle.css';

export function HuddleScreen({ nav, held }: { nav: NavProps; held: readonly string[] }) {
  const tz = nav.timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  const [[from, to], setRange] = useState(monthOf(todayIn(tz)));
  const [goals, setGoals] = useState(false);
  const [toast, setToast] = useToast();
  const h = useQuery<Huddle>('gp.huddle', { from, to });
  const act = useAct(setToast, () => h.reload());
  const blueprint = useDesign() === 'blueprint';
  const thisMonth = monthOf(todayIn(tz))[0] === from;
  const months = (
    <div className="seg" role="group" aria-label="Month">
      <button className="btn quiet" onClick={() => setRange(stepMonth(from, -1))}>
        ‹ {monthName(stepMonth(from, -1)[0]).split(' ')[0]}
      </button>
      <button className="btn quiet" disabled={thisMonth} onClick={() => setRange(monthOf(todayIn(tz)))}>
        This month
      </button>
      <button className="btn quiet" onClick={() => setRange(stepMonth(from, 1))}>
        {monthName(stepMonth(from, 1)[0]).split(' ')[0]} ›
      </button>
    </div>
  );
  if (blueprint) return <HuddlePage nav={nav} held={held} />;
  return (
    <div className="app books huddle">
      <Header
        nav={nav}
        title="Huddle"
        lede={`${monthName(from)} · ${h.data ? BASIS_WORDS[h.data.basis] : ''}`}
      />
      {/* the screen's own content is its <main>, as every place's is (QA-653: axe's region rule) */}
      <main className="books-body huddle-body">
        <div className="huddle-tools">
          {months}
          {mayEditGoals(held) && (
            <button className="btn soft" aria-expanded={goals} onClick={() => setGoals((g) => !g)}>
              {goals ? 'Close the goals' : `${monthName(from).split(' ')[0]}’s goals`}
            </button>
          )}
        </div>
        {goals && mayEditGoals(held) && (
          <Goals month={from.slice(0, 7)} busy={act.busy} run={act.run} onDone={() => setGoals(false)} />
        )}
        {!h.data ? (
          <Loading error={h.error} what="the huddle" retry={() => void h.reload()} />
        ) : (
          <>
            <Headline h={h.data} />
            <Targets h={h.data} />
            <Days h={h.data} busy={act.busy} run={act.run} />
            <p className="muted small huddle-foot">
              {COUNTING} {h.data.jobCount} {h.data.jobCount === 1 ? 'job' : 'jobs'} earned in{' '}
              {monthName(from)}.
            </p>
          </>
        )}
      </main>
      {toast && (
        <div className="toast" role="status">
          {toast}
        </div>
      )}
    </div>
  );
}

function Headline({ h }: { h: Huddle }) {
  const w = paceWords(h);
  return (
    <section className="sheet huddle-pace" aria-label="Revenue pace">
      <h2 className="tile-label">Revenue pace</h2>
      <p className={`pace-big ${w.tone}`}>
        {w.big && <span className="pace-pct">{w.big}</span>}
        <span className="pace-word">{w.word}</span>
      </p>
      <p className="pace-line">{w.line}</p>
      {paceNotes(h).map((n) => (
        <p key={n} className="muted small">
          {n}
        </p>
      ))}
    </section>
  );
}

const num = (v: number | null) => (v === null ? '—' : String(v));
function Stat({ label, children, note }: { label: string; children: ReactNode; note?: string }) {
  return (
    <div className="sheet tile">
      <span className="tile-label">{label}</span>
      <span className="big-num">{children}</span>
      {note && <span className="muted small">{note}</span>}
    </div>
  );
}
const cents = (v: number | null) => (v === null ? '—' : <Money cents={v} />);

function Targets({ h }: { h: Huddle }) {
  const t = h.targets;
  const b = h.budgeted;
  return (
    <>
      <Panel title="Targets">
        <div className="tiles">
          <Stat label="Working days">{h.workingDays}</Stat>
          <Stat label="Revenue goal">{cents(t.revenueCents)}</Stat>
          <Stat label="Planned technicians">{num(t.plannedTechs)}</Stat>
          <Stat
            label="Technicians working"
            note={t.actualTechsFrom === 'set' ? 'as set' : 'from the timesheets'}
          >
            {t.actualTechs}
          </Stat>
          <Stat label="Calls a technician a day">{num(t.callsPerTechDay)}</Stat>
          <Stat label="Calls this month">{num(t.monthlyCalls)}</Stat>
        </div>
      </Panel>
      <Panel title="Budgeted each working day">
        <div className="tiles">
          <Stat label="Revenue">{cents(b.dailyRevenueCents)}</Stat>
          <Stat label="Calls">{num(b.dailyCalls)}</Stat>
          <Stat label="Average ticket">{cents(b.avgTicketCents)}</Stat>
          <Stat label="Conversion rate">{b.conversionPct === null ? '—' : `${b.conversionPct} %`}</Stat>
          <Stat label="Average sale">{cents(b.avgSaleCents)}</Stat>
        </div>
      </Panel>
    </>
  );
}

type Run = ReturnType<typeof useAct>['run'];

function Days({ h, busy, run }: { h: Huddle; busy: boolean; run: Run }) {
  return (
    <Panel title="Each day">
      <DaysTable h={h} busy={busy} run={run} />
    </Panel>
  );
}

function DaysTable({ h, busy, run }: { h: Huddle; busy: boolean; run: Run }) {
  return (
    <div className="table-wrap" ref={scrollFocus}>
      <table className="grid-table huddle-days">
        <thead>
          <tr>
            <th className="day-col">Day</th>
            <th className="num">Revenue earned</th>
            <th className="num">Jobs</th>
            <th className="num">Sold</th>
            <th className="num">Days passed</th>
            <th>Note</th>
          </tr>
        </thead>
        <tbody>
          {h.days.map((d) => (
            <DayRow key={d.date} d={d} busy={busy} run={run} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DayRow({ d, busy, run }: { d: HuddleDay; busy: boolean; run: Run }) {
  const l = dayLabel(d.date);
  const cls = [d.today ? 'today' : '', !d.working ? 'off' : '', d.future ? 'future' : '']
    .filter(Boolean)
    .join(' ');
  return (
    <tr className={cls || undefined} aria-current={d.today ? 'date' : undefined}>
      <th scope="row" className="day-col">
        <span className="day-name">
          {l.weekday} <b>{l.num}</b>
        </span>
        {d.closed && <span className="muted small block">{d.closed}</span>}
        {d.today && <span className="muted small block">Today</span>}
      </th>
      <td className="num">
        {d.revenueCents ? <Money cents={d.revenueCents} /> : d.future ? '' : '—'}
        {d.provisionalCents > 0 && <span className="muted small block">draft invoices</span>}
        {d.inProgressCents > 0 && (
          <span className="muted small block">
            + <Money cents={d.inProgressCents} /> in progress
          </span>
        )}
      </td>
      <td className="num">{d.future ? '' : d.jobs}</td>
      <td className="num">{d.future ? '' : d.sold}</td>
      <td className="num muted">{d.workingDaysPassed}</td>
      <td className="note">
        <Note d={d} busy={busy} run={run} />
      </td>
    </tr>
  );
}

function Note({ d, busy, run }: { d: HuddleDay; busy: boolean; run: Run }) {
  const [text, setText] = useState<string | null>(null);
  const label = `Note for ${dayLabel(d.date).weekday} ${dayLabel(d.date).num}`;
  if (text === null)
    return (
      <button
        className={`btn quiet note-btn${d.note ? '' : ' empty'}`}
        aria-label={d.note ? `${label}: ${d.note}` : `Add a ${label.toLowerCase()}`}
        onClick={() => setText(d.note ?? '')}
      >
        {d.note ?? '+ Note'}
      </button>
    );
  const save = async () => {
    const t = text.trim();
    setText(null);
    if (t === (d.note ?? '')) return;
    await run('gp.addHuddleNote', { day: d.date, text: t }, t ? 'Note saved.' : 'Note removed.');
  };
  return (
    <input
      className="field note-field"
      aria-label={label}
      value={text}
      maxLength={500}
      autoFocus
      disabled={busy}
      onChange={(e) => setText(e.target.value)}
      onBlur={() => void save()}
      onKeyDown={(e) => {
        if (e.key === 'Enter') void save();
        if (e.key === 'Escape') setText(null);
      }}
    />
  );
}

function Goals({
  month,
  busy,
  run,
  onDone,
  bare = false,
}: {
  month: string;
  busy: boolean;
  run: Run;
  onDone: () => void;
  /** Only the form, for a section that is already titled (Blueprint's Targets). */
  bare?: boolean;
}) {
  const s = useQuery<{ value: ({ month: string } & Partial<Record<GoalField, number | null>>)[] }>(
    'settings.get',
    {
      key: 'huddle.targets',
    },
  );
  const [texts, setTexts] = useState<Record<GoalField, string> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const row = s.data?.value.find((r) => r.month === month);
  const shown =
    texts ??
    (Object.fromEntries(GOAL_FIELDS.map((f) => [f.key, goalText(f, row?.[f.key])])) as Record<
      GoalField,
      string
    >);
  if (!s.data) return <Loading error={s.error} what="the goals" />;
  const form = (
    <form
      className="huddle-goals"
      onSubmit={(e) => {
        e.preventDefault();
        const r = goalInput(shown);
        if ('error' in r) return setError(r.error);
        setError(null);
        void run('gp.setTarget', { month, ...r.fields }, 'Goals saved.').then((done) => done && onDone());
      }}
    >
      {GOAL_FIELDS.map((f) => (
        <label key={f.key} className="f">
          {f.label}
          <input
            className="field"
            inputMode="decimal"
            value={shown[f.key]}
            placeholder={f.money ? '$' : ''}
            onChange={(e) => setTexts({ ...shown, [f.key]: e.target.value })}
          />
          {f.hint && <span className="muted small">{f.hint}</span>}
        </label>
      ))}
      {error && (
        <p className="bad-text" role="alert">
          {error}
        </p>
      )}
      <div className="row">
        <button className="btn primary" type="submit" disabled={busy}>
          Save the goals
        </button>
      </div>
    </form>
  );
  return bare ? form : <Panel title={`${monthName(`${month}-01`)}’s goals`}>{form}</Panel>;
}
